<script setup>
import { ref, watch, computed, defineProps } from "vue"
import { useStore } from "vuex"
import { useRouter, useRoute } from "vue-router"
import { useHead } from "@unhead/vue"
import { apiUrl, extractTextFromHTMLString } from "@common/js/helpers"
import Loading from "@common/js/components/loaders/Loading.vue"
import Breadcrumb from "@common/js/utils/Breadcrumb.vue"
import Heading from "@/js/partials/hardware/shop/manufacturers/Heading.vue"

const props = defineProps({
  slug: {
    type: String,
    required: true,
  },
})
const loading = ref(true)

const store = useStore()
const router = useRouter()
const route = useRoute()

const manufacturer = computed(() =>
  store.getters["hardwareManufacturers/getBySlug"](props.slug)
)

watch(
  () => props.slug,
  (val) => {
    fetchManufacturer(val)
  },
  { immediate: true, deep: true }
)

async function fetchManufacturer(slug) {
  try {
    loading.value = true
    await store.dispatch("hardwareManufacturers/fetchBySlug", slug)
    if (!manufacturer.value) await router.push({ name: "PageNotFound" })
    await setHead()
  } finally {
    loading.value = false
  }
}

async function setHead() {
  useHead({
    title: `${manufacturer.value.companyName} - Marque`,
    link: [
      {
        rel: "canonical",
        href: `${import.meta.env.VITE_BASE_URL}${route.path}`,
      },
    ],
    meta: [
      {
        name: "og:title",
        content: `${manufacturer.value.companyName} - Marque`,
      },

      ...(!!manufacturer.value.about
        ? [
            {
              name: "description",
              content: extractTextFromHTMLString(
                manufacturer.value.about
              ).split("\.", 1)[0],
            },
            {
              name: "og:description",
              content: extractTextFromHTMLString(
                manufacturer.value.about
              ).split("\.", 1)[0],
            },
          ]
        : []),
      ...(!!manufacturer.value.logo
        ? [
            {
              name: "og:image",
              content: apiUrl(manufacturer.value.logo.thumbnail.url),
            },
          ]
        : []),
    ],
  })
}

const pages = computed(() => [
  {
    name: "Marque",
    to: { name: "HardwareMarketplaceShopManufacturers" },
  },
  {
    name: manufacturer.value.companyName,
    to: {
      name: "HardwareMarketplaceShopManufacturer",
      params: { id: manufacturer.value.id },
    },
  },
])
</script>

<template>
  <Loading v-if="loading" class="h-screen" />
  <main v-else class="max-w-7xl mx-auto px-4 sm:px-6 py-6">
    <!---->
    <div class="mt-8">
      <h1 class="sr-only">{{ manufacturer.companyName }}</h1>
      <div class="space-y-8 md:space-y-16">
        <div class="flex flex-col">
          <Breadcrumb
            class="hidden sm:flex mb-5"
            aria-label="Breadcrumb"
            :pages="pages"
            divider="slash"
          />
          <Heading :manufacturer="manufacturer" />
        </div>
        <div class="mx-auto">
          <div
            class="flex flex-col overflow-hidden rounded-3xl lg:relative lg:flex-row"
          >
            <div class="relative lg:static lg:w-1/2">
              <img class="size-full object-cover object-center" />
            </div>
            <div class="relative lg:w-1/2">
              <div
                class="flex h-full flex-col justify-center p-6 lg:p-12 bg-gray-50"
              >
                <div v-safe-html="manufacturer.about"></div>
                <div class="mt-4 flex flex-wrap gap-4">
                  <a
                    href="/fr/search?q=APsystems"
                    type="button"
                    preserve-scroll="false"
                    disabled="false"
                    class="btn group items-center justify-center bg-primary fill-neutral-1000 text-neutral-1000 focus:ring focus:ring-neutral-500 focus:ring-offset-2 group-hover:bg-neutral-900 group-hover:text-neutral-1000 hocus:bg-neutral-900"
                    >Aller aux produits
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
